/**
 * IndexedDB 数据库管理工具
 */
class IndexedDBManager {
    constructor() {
        this.dbName = 'ShoppingCartDB';
        this.dbVersion = 1;
        this.storeName = 'cart';
    }

    /**
     * 打开数据库连接
     */
    async openDB () {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => {
                reject(new Error('数据库打开失败'));
            };

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // 创建购物车存储对象
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
                    store.createIndex('id', 'id', { unique: false });
                    store.createIndex('mediumCode', 'mediumCode', { unique: false });
                }
            };
        });
    }

    /**
     * 获取购物车中的所有商品
     */
    async getCartItems () {
        try {
            const db = await this.openDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.getAll();

                request.onsuccess = () => {
                    resolve(request.result || []);
                };

                request.onerror = () => {
                    reject(new Error('获取购物车数据失败'));
                };
            });
        } catch (error) {
            console.error('获取购物车数据失败:', error);
            return [];
        }
    }

    /**
     * 添加商品到购物车
     */
    async addToCart (item) {
        try {
            const db = await this.openDB();


            return new Promise(async (resolve, reject) => {
                const cartItems = await this.getCartItems();
                const existingItem = cartItems.find(cartItem =>
                    cartItem.id === item.id && cartItem.mediumCode === item.mediumCode
                );

                if (existingItem) {
                    resolve();
                    return
                }


                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                // 直接添加新商品（因为调用前已经检查过是否存在）
                const newItem = {
                    ...item,
                    quantity: 1,
                    addedTime: new Date().toISOString(),
                    updatedTime: new Date().toISOString()
                };

                const addRequest = store.add(newItem);
                addRequest.onsuccess = () => resolve({ type: 'add', item: newItem });
                addRequest.onerror = () => reject(new Error('添加商品失败'));
            });
        } catch (error) {
            console.error('添加商品到购物车失败:', error);
            throw error;
        }
    }

    /**
     * 更新商品数量
     */
    async updateQuantity (itemId, quantity) {
        try {
            const db = await this.openDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                const getRequest = store.get(itemId);

                getRequest.onsuccess = () => {
                    const item = getRequest.result;
                    if (item) {
                        item.quantity = quantity;
                        item.updatedTime = new Date().toISOString();

                        const updateRequest = store.put(item);
                        updateRequest.onsuccess = () => resolve(item);
                        updateRequest.onerror = () => reject(new Error('更新数量失败'));
                    } else {
                        reject(new Error('商品不存在'));
                    }
                };

                getRequest.onerror = () => {
                    reject(new Error('获取商品失败'));
                };
            });
        } catch (error) {
            console.error('更新商品数量失败:', error);
            throw error;
        }
    }

    /**
     * 从购物车中删除商品
     */
    async removeFromCart (itemId) {
        try {
            const db = await this.openDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                const deleteRequest = store.delete(itemId);
                deleteRequest.onsuccess = () => resolve();
                deleteRequest.onerror = () => reject(new Error('删除商品失败'));
            });
        } catch (error) {
            console.error('删除商品失败:', error);
            throw error;
        }
    }

    /**
 * 通过pid从购物车中删除商品
 */
    async removeFromCartByPid (pid) {
        try {
            const db = await this.openDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                // 先获取所有商品
                const getAllRequest = store.getAll();

                getAllRequest.onsuccess = () => {
                    const items = getAllRequest.result || [];
                    // 找到匹配pid的商品
                    const targetItem = items.find(item => item.pid === pid || item.id === pid);

                    if (targetItem) {
                        // 删除找到的商品
                        const deleteRequest = store.delete(targetItem.id);
                        deleteRequest.onsuccess = () => resolve(targetItem);
                        deleteRequest.onerror = () => reject(new Error('删除商品失败'));
                    } else {
                        resolve(null); // 没有找到匹配的商品
                    }
                };

                getAllRequest.onerror = () => {
                    reject(new Error('获取购物车数据失败'));
                };
            });
        } catch (error) {
            console.error('通过pid删除商品失败:', error);
            throw error;
        }
    }

    /**
     * 通过pid批量删除购物车中的商品
     */
    async removeFromCartByPids (pids) {
        try {
            const db = await this.openDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                // 先获取所有商品
                const getAllRequest = store.getAll();

                getAllRequest.onsuccess = () => {
                    const items = getAllRequest.result || [];
                    // 找到匹配pids的商品
                    const targetItems = items.filter(item =>
                        pids.includes(item.pid) || pids.includes(item.id)
                    );

                    if (targetItems.length > 0) {
                        console.log(1111, targetItems);
                        // 删除所有匹配的商品
                        const deletePromises = targetItems.map(item => {
                            return new Promise((resolveDelete, rejectDelete) => {
                                const deleteRequest = store.delete(item.id);
                                deleteRequest.onsuccess = () => resolveDelete(item);
                                deleteRequest.onerror = () => rejectDelete(new Error('删除商品失败'));
                            });
                        });

                        Promise.all(deletePromises)
                            .then(() => resolve(targetItems))
                            .catch(error => reject(error));
                    } else {
                        resolve([]); // 没有找到匹配的商品
                    }
                };

                getAllRequest.onerror = () => {
                    reject(new Error('获取购物车数据失败'));
                };
            });
        } catch (error) {
            console.error('通过pids批量删除商品失败:', error);
            throw error;
        }
    }

    /**
     * 清空购物车
     */
    async clearCart () {
        try {
            const db = await this.openDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                const clearRequest = store.clear();
                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(new Error('清空购物车失败'));
            });
        } catch (error) {
            console.error('清空购物车失败:', error);
            throw error;
        }
    }

    /**
     * 获取购物车商品总数
     */
    async getCartCount () {
        try {
            const items = await this.getCartItems();
            return items.reduce((total, item) => total + (item.quantity || 1), 0);
        } catch (error) {
            console.error('获取购物车数量失败:', error);
            return 0;
        }
    }
}

// 创建单例实例
const indexedDBManager = new IndexedDBManager();

export default indexedDBManager; 